<script setup lang="ts">
    import { columns } from '@/tables/columns/vat';
    import { Head } from '@inertiajs/vue3';
    import { ref, watch, computed, onMounted } from 'vue';
    import { Table, Row } from '@tanstack/vue-table';
    import { updateFirstColumnWidth } from '@/composables/useTableSizing';
    import { useI18n } from 'vue-i18n';
    import { usePage } from '@inertiajs/vue3';
    import { useQuery, useMutation, useQueryClient } from '@tanstack/vue-query';
    import { useUpdateDatatableRow } from '@/composables/useUpdateDatatableRow';
    import { useVatStore } from '@/stores/vat';
    import { storeToRefs } from 'pinia';
    import AppLayout from '@/layouts/AppLayout.vue';
    import axios from 'axios';
    import DataTable from '@/components/datatable/DataTable.vue';
    import DataTableToolbarActions from '@/components/datatable/DataTableToolbarActions.vue';
    import MonthPicker from '@/components/MonthPicker.vue';
    import PageCard from '@/components/PageCard.vue';
    import QueryKey from '@/enums/QueryKey';
    import TableTypeSelect from '@/components/datatable/DataTableTypeSelect.vue';
    import ToggleShowPreviousSubmissions from '@/components/vat/ToggleShowPreviousSubmissions.vue';
    import type { SharedData, BreadcrumbItem, VatSubmission } from '@/types';

    const page = usePage<SharedData>();
    const { t } = useI18n();
    const vatStore = useVatStore();
    const { date, selectedGrouping } = storeToRefs(vatStore);

    const { updateDataTableRow } = useUpdateDatatableRow();

    const selectedCompanyId = computed(() => page.props.auth.user.selected_company_id);

    const breadcrumbs: BreadcrumbItem[] = [
        {
            title: 'menu.vat',
        },
    ];

    const { isPending, data } = useQuery({
        queryKey: [QueryKey.VAT, selectedCompanyId, date, selectedGrouping],
        queryFn: getVatData,
    });

    async function getVatData() {
        const { data } = await axios.get(route('v2.vat.table'), {
            params: {
                date: date.value,
                grouping: selectedGrouping.value,
            },
        });
        return data.data;
    }

    const dataTableRef = ref<{ table: Table<VatSubmission> } | undefined>(undefined);

    watch(
        () => dataTableRef.value?.table.getRowModel().rows.map((r) => r.id),
        () => {
            if (!dataTableRef.value) return;
            updateFirstColumnWidth(dataTableRef.value.table, 'company_name');
        },
        { deep: true },
    );

    const typeOptions = [
        { value: 'my_clients', label: 'dashboards.grouping.my_clients' },
        { value: 'all', label: 'dashboards.grouping.all' },
        { value: 'outsourcing', label: 'dashboards.grouping.outsourcing' },
        { value: 'powerbi', label: 'dashboards.grouping.powerbi' },
        { value: 'salary', label: 'dashboards.grouping.salary' },
        { value: 'roger_ai', label: 'dashboards.grouping.roger_ai' },
    ];

    const queryClient = useQueryClient();

    const updateVatField = useMutation({
        mutationFn: async ({
            rowIndex,
            columnId,
            value,
        }: {
            rowIndex: number;
            columnId: keyof VatSubmission;
            value: any;
        }) => {
            const rowData = data.value?.[rowIndex];
            if (!rowData) return;

            const payload = {
                field: columnId,
                value: value,
            };

            await axios.post(route('v2.vat.submission.update-field', rowData.id), payload);
        },
        onMutate: async ({ rowIndex, columnId, value }) => {
            await queryClient.cancelQueries({
                queryKey: [
                    QueryKey.VAT,
                    selectedCompanyId,
                    date,
                    selectedGrouping,
                ],
            });

            setQueryData(rowIndex, columnId, value);
        },

        onError: (error) => {
            console.error('Failed to update VAT field:', error);
        },
    });

    const updateData = (row: Row<VatSubmission>, columnId: keyof VatSubmission, value: any) => {
        if (columnId === 'total_vat') {
            setQueryData(row.index, columnId, value);
            return;
        }

        const original = row.original[columnId];
        const originalVal = original?.valueOf?.() ?? null;
        const newVal = value ?? null;

        if (Object.is(originalVal, newVal)) return;

        updateVatField.mutate({ rowIndex: row.index, columnId, value });
    };

    const setQueryData = (rowIndex: number, columnId: keyof VatSubmission, value: any) => {
        updateDataTableRow(
            [QueryKey.VAT, selectedCompanyId, date, selectedGrouping],
            rowIndex,
            columnId,
            value,
        );
    };
</script>

<template>
    <Head :title="t('menu.vat')" />

    <AppLayout
        :breadcrumbs="breadcrumbs"
        header-class="w-full"
    >
        <template #header-actions>
            <span class="ml-auto">
                <MonthPicker v-model="vatStore.date" />
            </span>
        </template>

        <PageCard>
            <DataTable
                ref="dataTableRef"
                :table-key="QueryKey.VAT"
                :columns="columns"
                :data="data"
                :loading="isPending"
                :meta="{ updateData }"
                :default-sorting="[
                    {
                        id: 'company_name',
                        desc: false,
                    },
                ]"
                :pinned-columns="{
                    left: ['company_name', 'period'],
                }"
                :default-column-visibility="{
                    company_group: false,
                }"
            >
                <template #title>
                    {{ t('menu.vat') }}
                    <TableTypeSelect
                        v-model="vatStore.selectedGrouping"
                        :options="typeOptions"
                    />
                </template>
                <template #actions>
                    <DataTableToolbarActions name="vat">
                        <ToggleShowPreviousSubmissions />
                    </DataTableToolbarActions>
                </template>
            </DataTable>
        </PageCard>
    </AppLayout>
</template>
